import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import styles from './SelectProductCatalogueModal.module.css';
import { useAmazonSync } from '../../context/AmazonSyncContext';

const PRODUCTS_PER_PAGE = 16;

// Backdrop component
const Backdrop = (props) => {
  return <div className={styles.backdrop} onClick={props.onClose} />;
};

// Modal overlay component
const ModalOverlay = (props) => {
  const { products, selectedAsins, onAsinSelect, onSelectAll, onAnalyze, selectAll, actionLabel = 'Analyse Listing Score', singleSelect = false } = props;
  const selectedCount = selectedAsins.length;
  const totalCount = products.length;
  const [currentPage, setCurrentPage] = useState(1);
  const [showAsin, setShowAsin] = useState(false);
  const { status: syncStatus, error: syncError } = useAmazonSync();

  const totalPages = Math.ceil(totalCount / PRODUCTS_PER_PAGE);
  const startIdx = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const pageProducts = products.slice(startIdx, startIdx + PRODUCTS_PER_PAGE);

  // Bridge-mode state: catalogue not yet available, show informational view
  // instead of an empty grid that looks broken.
  const isBridge = (syncStatus === 'syncing' || syncStatus === 'failed') && totalCount === 0;

  if (isBridge) {
    return (
      <div className={`${styles.modal} ${singleSelect ? styles.modalSingleSelect : ''}`}>
        <header className={styles.header}>
          <h2>Select from Product Catalogue</h2>
          <button onClick={props.onClose} className={styles.closeButton} aria-label="Close Modal">
            <FaTimes />
          </button>
        </header>
        <div style={{ padding: '48px 32px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          {syncStatus === 'syncing' ? (
            <>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" style={{ color: '#2766AA' }}>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
                <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
                </path>
              </svg>
              <h3 style={{ color: '#505050', fontSize: '18px', fontWeight: 600, margin: 0 }}>Catalogue still syncing</h3>
              <p style={{ color: '#737373', fontSize: '14px', maxWidth: '360px', margin: 0, lineHeight: 1.5 }}>
                We're fetching your products from Amazon. This usually takes 5–10 minutes. In the meantime, you can paste any ASIN directly to start working.
              </p>
            </>
          ) : (
            <>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" style={{ color: '#D33A3A' }}>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                <path d="M12 7v6M12 16v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <h3 style={{ color: '#B82424', fontSize: '18px', fontWeight: 600, margin: 0 }}>Catalogue sync failed</h3>
              <p style={{ color: '#737373', fontSize: '14px', maxWidth: '360px', margin: 0, lineHeight: 1.5 }}>
                {syncError || 'We couldn’t fetch your catalogue. Reconnect your Amazon account from the banner above and try again.'}
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.modal} ${singleSelect ? styles.modalSingleSelect : ''}`}>
      <header className={styles.header}>
        <h2>Select from Product Catalogue</h2>
        <button onClick={props.onClose} className={styles.closeButton} aria-label="Close Modal">
          <FaTimes />
        </button>
      </header>

      <div className={styles.displayToggle}>
        <div className={`${styles.toggleSlider} ${showAsin ? styles.toggleSliderRight : ''}`} />
        <button
          className={`${styles.toggleButton} ${!showAsin ? styles.toggleButtonActive : ''}`}
          onClick={() => setShowAsin(false)}
        >
          SKU ID
        </button>
        <button
          className={`${styles.toggleButton} ${showAsin ? styles.toggleButtonActive : ''}`}
          onClick={() => setShowAsin(true)}
        >
          ASIN
        </button>
      </div>

      {!singleSelect && (
        <div className={styles.selectionSummary}>
          <div className={styles.selectionSummaryRow}>
            <span className={styles.selectedCount}>
              {selectedCount}/{totalCount} Selected
            </span>
            <label className={styles.selectAllLabel}>
              <input
                type="checkbox"
                checked={selectAll}
                onChange={onSelectAll}
              />
              <span>Select All</span>
            </label>
          </div>
        </div>
      )}

      <div className={singleSelect ? styles.contentSingleSelect : styles.content}>
        <div className={styles.productsGrid}>
          {Array.from({ length: Math.ceil(pageProducts.length / 2) }).map((_, rowIndex) => (
            <div key={rowIndex} className={styles.productsRow}>
              {pageProducts.slice(rowIndex * 2, rowIndex * 2 + 2).map((product) => {
                const isSelected = selectedAsins.includes(product.asin);
                return (
                  <div key={product.asin} className={singleSelect ? styles.productCardWrapperSingle : styles.productCardWrapper}>
                    {!singleSelect && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onAsinSelect(product.asin)}
                        className={styles.productCheckbox}
                      />
                    )}
                    <div
                      className={`${styles.productCard} ${singleSelect && isSelected ? styles.productCardSelected : ''}`}
                      onClick={() => onAsinSelect(product.asin)}
                    >
                      <div className={styles.productCardInner}>
                        <div className={styles.productImage}>
                          {product.image_url && (
                            <img
                              src={product.image_url}
                              alt={product.title || product.asin}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                        </div>
                        <div className={styles.productInfo}>
                          <div className={styles.productInfoInner}>
                            <div className={styles.productName}>
                              {product.title || product.asin}
                            </div>
                            <div className={styles.skuTag}>
                              <span>{showAsin ? `ASIN : ${product.asin || 'N/A'}` : `SKU ID : ${product.sku || 'N/A'}`}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <footer className={styles.actions}>
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.pageButton}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <FaChevronLeft />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                className={`${styles.pageButton} ${page === currentPage ? styles.pageButtonActive : ''}`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
            <button
              className={styles.pageButton}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <FaChevronRight />
            </button>
          </div>
        )}
        <button
          onClick={onAnalyze}
          disabled={selectedCount === 0}
          className={`${styles.button} ${styles.buttonPrimary} ${selectedCount === 0 ? styles.disabled : ''}`}
        >
          {actionLabel}
        </button>
      </footer>
    </div>
  );
};

// Main Modal component using React Portals
const SelectProductCatalogueModal = (props) => {
  // Check if running in a browser environment before accessing document
  if (typeof window === 'undefined') {
    return null;
  }

  const portalElement = document.getElementById('modal-root');

  // Ensure the portal element exists before attempting to create a portal
  if (!portalElement) {
    console.warn('SelectProductCatalogueModal: modal-root element not found');
    return null;
  }

  // Only render if show is true
  if (!props.show) {
    return null;
  }

  return (
    <React.Fragment>
      {ReactDOM.createPortal(
        <Backdrop onClose={props.onClose} />,
        portalElement
      )}
      {ReactDOM.createPortal(
        <ModalOverlay
          products={props.products || []}
          selectedAsins={props.selectedAsins || []}
          onAsinSelect={props.onAsinSelect}
          onSelectAll={props.onSelectAll}
          onAnalyze={props.onAnalyze}
          onClose={props.onClose}
          selectAll={props.selectAll}
          actionLabel={props.actionLabel}
          singleSelect={props.singleSelect}
        />,
        portalElement
      )}
    </React.Fragment>
  );
};

export default SelectProductCatalogueModal;

